// Gmail and Google Calendar operations with the same names, inputs and results as the Microsoft ones
// (apps/desktop/src/m365ops.ts), so Studio, VERA's lookups, voice and the email and meeting cards work unchanged.
// Every operation that sends, drafts or books asks first through `confirm`.

import { decodeHtmlText, extractLinks, GraphInputError, localStamp, type Confirm, type LinkInfo } from "../../../desktop/src/m365ops";

type Call = (method: "GET" | "POST" | "PATCH" | "DELETE", url: string, body?: unknown) => Promise<any>;
type GOp = (args: Record<string, unknown>, call: Call, confirm: Confirm) => Promise<unknown>;

const GMAIL = "https://gmail.googleapis.com/gmail/v1/users/me";
const CAL = "https://www.googleapis.com/calendar/v3";
const tz = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
const pad = (n: number) => String(n).padStart(2, "0");

// ---------- input checks (the same rules as Outlook) ----------
const str = (v: unknown, name: string, max = 500, min = 1) => {
  if (typeof v !== "string" || v.trim().length < min || v.length > max) throw new GraphInputError(`Please check the ${name}.`);
  return v.trim();
};
const optStr = (v: unknown, name: string, max = 5000) => (v === undefined || v === null || v === "" ? "" : str(v, name, max));
const EMAIL = /^[^\s@<>()"',;:]+@[^\s@<>()"',;:]+\.[^\s@<>()"',;:]+$/;
const emails = (v: unknown, max = 30): string[] => {
  if (v === undefined || v === null) return [];
  if (!Array.isArray(v) || v.length > max) throw new GraphInputError("Please check the email addresses.");
  return v.map((e) => { const s = str(e, "email address", 254); if (!EMAIL.test(s)) throw new GraphInputError(`"${s}" isn't an email address.`); return s; });
};
const minutes = (v: unknown) => { const n = Number(v); if (!Number.isInteger(n) || n < 5 || n > 480) throw new GraphInputError("Please check the length of the meeting."); return n; };
const isoDate = (v: unknown, name: string) => { const s = str(v, name, 10); if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) throw new GraphInputError(`Please check the ${name}.`); return s; };
const localDateTime = (v: unknown) => { const s = str(v, "date and time", 25); if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(s)) throw new GraphInputError("Please check the date and time."); return s.length === 16 ? `${s}:00` : s; };
const toLocal = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;

// ---------- text encoding ----------
const utf8 = (s: string) => new TextEncoder().encode(s);
const b64 = (bytes: Uint8Array) => { let bin = ""; for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000)); return btoa(bin); };
const b64url = (bytes: Uint8Array) => b64(bytes).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
const fromB64url = (s: string) => { const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/")); const out = new Uint8Array(bin.length); for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i); return out; };
const wrap76 = (s: string) => s.replace(/(.{76})/g, "$1\r\n");
const header = (s: string) => (/^[\x20-\x7e]*$/.test(s) ? s : `=?UTF-8?B?${b64(utf8(s))}?=`);

// ---------- message helpers ----------
type Part = { mimeType?: string; filename?: string; headers?: { name: string; value: string }[]; body?: { data?: string; attachmentId?: string; size?: number }; parts?: Part[] };
const hdr = (p: Part | undefined, name: string) => p?.headers?.find((h) => h.name.toLowerCase() === name.toLowerCase())?.value ?? "";
/** "Ana Krasniqi <ana@x.com>" → { name, email } */
const person = (v: string) => { const m = /^\s*"?([^"<]*?)"?\s*<([^>]+)>\s*$/.exec(v); return m ? { name: m[1].trim() || m[2], email: m[2].trim() } : { name: v.trim(), email: v.trim() }; };
const people = (v: string) => (v ? v.split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/).map(person).filter((p) => p.email) : []);
const walk = (p: Part | undefined, out: Part[] = []): Part[] => { if (!p) return out; out.push(p); for (const c of p.parts ?? []) walk(c, out); return out; };
const webLink = (id: string) => `https://mail.google.com/mail/u/0/#all/${id}`;

const summary = (m: any) => ({
  id: m.id, subject: hdr(m.payload, "Subject") || "(no subject)", from: person(hdr(m.payload, "From")),
  to: people(hdr(m.payload, "To")).map((p) => p.email), received: localStamp(new Date(Number(m.internalDate)).toISOString()), sent: null,
  preview: decodeHtmlText(String(m.snippet ?? "")), hasAttachments: walk(m.payload).some((p) => p.filename && p.body?.attachmentId),
  unread: (m.labelIds ?? []).includes("UNREAD"), webLink: webLink(m.id),
});

async function list(call: Call, q: string, max: number, labelIds?: string) {
  const ids = await call("GET", `${GMAIL}/messages?maxResults=${max}${q ? `&q=${encodeURIComponent(q)}` : ""}${labelIds ? `&labelIds=${labelIds}` : ""}`);
  const msgs = await Promise.all(((ids.messages ?? []) as { id: string }[]).map((x) =>
    call("GET", `${GMAIL}/messages/${x.id}?format=metadata&metadataHeaders=From&metadataHeaders=To&metadataHeaders=Subject&metadataHeaders=Date`)));
  return msgs;
}

// ---------- outgoing mail ----------
type OutFile = { name: string; contentType: string; contentBytes: string };
function outFiles(v: unknown): OutFile[] {
  if (v === undefined || v === null) return [];
  if (!Array.isArray(v) || v.length > 5) throw new GraphInputError("Please attach at most 5 files.");
  return v.map((f) => {
    const name = str((f as any)?.name, "file name", 255);
    const m = /^data:([^;,]*);base64,([A-Za-z0-9+/=]+)$/.exec(String((f as any)?.dataUrl ?? ""));
    if (!m) throw new GraphInputError(`${name} didn't come through. Please attach it again.`);
    if (m[2].length * 0.75 > 10 * 1024 * 1024) throw new GraphInputError(`${name} is larger than 10 MB. Share it as a link instead.`);
    return { name, contentType: m[1] || "application/octet-stream", contentBytes: m[2] };
  });
}

/** An RFC 2822 message, ready for Gmail's raw field. */
export function mimeMessage(o: { to: string[]; cc: string[]; subject: string; body: string; inReplyTo?: string; references?: string; files?: OutFile[] }): string {
  const lines = [
    ...(o.to.length ? [`To: ${o.to.join(", ")}`] : []),
    ...(o.cc.length ? [`Cc: ${o.cc.join(", ")}`] : []),
    `Subject: ${header(o.subject)}`,
    "MIME-Version: 1.0",
    ...(o.inReplyTo ? [`In-Reply-To: ${o.inReplyTo}`, `References: ${[o.references, o.inReplyTo].filter(Boolean).join(" ")}`] : []),
  ];
  const text = ["Content-Type: text/plain; charset=UTF-8", "Content-Transfer-Encoding: base64", "", wrap76(b64(utf8(o.body)))].join("\r\n");
  if (!o.files?.length) return [...lines, text].join("\r\n");
  const boundary = `vera-${Math.random().toString(36).slice(2)}`;
  const parts = o.files.map((f) => [
    `--${boundary}`, `Content-Type: ${f.contentType}; name="${header(f.name)}"`, `Content-Disposition: attachment; filename="${header(f.name)}"`,
    "Content-Transfer-Encoding: base64", "", wrap76(f.contentBytes)].join("\r\n"));
  return [...lines, `Content-Type: multipart/mixed; boundary="${boundary}"`, "", `--${boundary}`, text, ...parts, `--${boundary}--`].join("\r\n");
}

/** What a reply needs from the email it answers. */
async function replyContext(call: Call, id: string) {
  const m = await call("GET", `${GMAIL}/messages/${encodeURIComponent(id)}?format=metadata&metadataHeaders=Message-ID&metadataHeaders=References&metadataHeaders=Subject&metadataHeaders=From&metadataHeaders=Reply-To`);
  const subject = hdr(m.payload, "Subject");
  return {
    threadId: m.threadId as string, messageId: hdr(m.payload, "Message-ID"), references: hdr(m.payload, "References"),
    subject: /^re:/i.test(subject) ? subject : `Re: ${subject}`, replyTo: person(hdr(m.payload, "Reply-To") || hdr(m.payload, "From")).email,
  };
}

// ---------- calendar ----------
const eventOut = (e: any) => ({
  id: e.id, subject: e.summary ?? "(no title)", start: e.start?.dateTime ?? e.start?.date, end: e.end?.dateTime ?? e.end?.date, webLink: e.htmlLink,
  teams: Boolean(e.hangoutLink), joinUrl: e.hangoutLink ?? null, location: e.location ?? "",
});

export const GOOGLE_OPS: Record<string, GOp> = {
  "mail.inbox": async (_a, call) =>
    (await list(call, "", 12, "INBOX")).map((m) => { const s = summary(m); return { id: s.id, subject: s.subject, from: s.from, received: new Date(Number(m.internalDate)).toISOString(), preview: s.preview, unread: s.unread, webLink: s.webLink }; }),

  "mail.search": async (a, call) => {
    const folder = a.folder === "sent" ? "in:sent" : a.folder === "all" ? "" : "in:inbox";
    const q = [folder, optStr(a.query, "search", 200)].filter(Boolean).join(" ");
    return (await list(call, q, Math.min(25, Math.max(1, Number(a.max) || 10)))).map(summary);
  },

  "mail.read": async (a, call) => {
    const m = await call("GET", `${GMAIL}/messages/${encodeURIComponent(str(a.id, "item", 1024))}?format=full`);
    const all = walk(m.payload);
    const plain = all.find((p) => p.mimeType === "text/plain" && p.body?.data && !p.filename);
    const html = all.find((p) => p.mimeType === "text/html" && p.body?.data && !p.filename);
    const htmlText = html ? new TextDecoder().decode(fromB64url(html.body!.data!)) : "";
    const text = plain ? new TextDecoder().decode(fromB64url(plain.body!.data!)) : decodeHtmlText(htmlText);
    let links: LinkInfo[] = [];
    try { links = extractLinks(htmlText); } catch { /* the text is enough */ }
    return {
      links, id: m.id, subject: hdr(m.payload, "Subject"), from: person(hdr(m.payload, "From")),
      received: localStamp(new Date(Number(m.internalDate)).toISOString()), sent: null,
      to: people(hdr(m.payload, "To")), cc: people(hdr(m.payload, "Cc")), text: text.slice(0, 100_000), webLink: webLink(m.id),
      attachments: all.filter((p) => p.filename && p.body?.attachmentId).map((p) => ({ id: p.body!.attachmentId!, name: p.filename!, contentType: p.mimeType ?? "application/octet-stream", size: p.body?.size ?? 0 })),
    };
  },

  "mail.attachment": async (a, call) => {
    const id = str(a.messageId, "item", 1024), attId = str(a.attachmentId, "attachment", 2048);
    const m = await call("GET", `${GMAIL}/messages/${encodeURIComponent(id)}?format=full`);
    const part = walk(m.payload).find((p) => p.body?.attachmentId === attId);
    const name = part?.filename || "attachment", contentType = part?.mimeType || "application/octet-stream", size = part?.body?.size ?? 0;
    if (size > 15 * 1024 * 1024) return { name, size, note: "This attachment is larger than 15 MB, so VERA can't open it here." };
    const data = (await call("GET", `${GMAIL}/messages/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attId)}`)).data as string;
    const bytes = fromB64url(data);
    const isText = /^text\/|\/(json|csv|xml)$/.test(contentType) || /\.(txt|md|csv|json)$/i.test(name);
    return isText ? { name, contentType, size, text: new TextDecoder().decode(bytes).slice(0, 100_000) } : { name, contentType, size, dataUrl: `data:${contentType};base64,${b64(bytes)}` };
  },

  "mail.send": async (a, call, confirm) => {
    const body = str(a.body, "message", 50_000);
    let to = emails(a.to); const cc = emails(a.cc);
    const files = outFiles(a.attachments);
    const reply = a.replyToId ? await replyContext(call, str(a.replyToId, "item", 1024)) : null;
    const subject = reply ? reply.subject : optStr(a.subject, "subject", 255);
    if (reply && !to.length && reply.replyTo) to = [reply.replyTo];
    if (!to.length) throw new GraphInputError("Who should this go to? Add at least one email address.");
    if (!reply && !subject) throw new GraphInputError("Please add a subject.");
    const lines = [`To: ${to.join(", ")}`, ...(cc.length ? [`Cc: ${cc.join(", ")}`] : []), `Subject: ${subject}`,
      ...(files.length ? [`Attached: ${files.map((f) => f.name).join(", ")}`] : []), "", body.length > 700 ? `${body.slice(0, 700)}...` : body, "",
      "It goes out from your Gmail as soon as you say Yes."];
    if (!(await confirm("Send this email now?", lines.join("\n")))) return { cancelled: true };
    const raw = b64url(utf8(mimeMessage({ to, cc, subject, body, inReplyTo: reply?.messageId, references: reply?.references, files })));
    await call("POST", `${GMAIL}/messages/send`, { raw, ...(reply ? { threadId: reply.threadId } : {}) });
    return { sent: true };
  },

  "mail.draft": async (a, call, confirm) => {
    const body = str(a.body, "message", 50_000);
    const to = emails(a.to), cc = emails(a.cc), files = outFiles(a.attachments);
    const reply = a.replyToId ? await replyContext(call, str(a.replyToId, "item", 1024)) : null;
    const subject = reply ? reply.subject : str(a.subject, "subject", 255);
    if (!(await confirm("Save this draft in your Gmail?", `${to.length ? `To: ${to.join(", ")}` : "(you'll add recipients)"}\nSubject: ${subject}${files.length ? `\nAttached: ${files.map((f) => f.name).join(", ")}` : ""}\n\nIt stays in your Drafts until you send it.`))) return { cancelled: true };
    const raw = b64url(utf8(mimeMessage({ to: to.length ? to : reply?.replyTo ? [reply.replyTo] : [], cc, subject, body, inReplyTo: reply?.messageId, references: reply?.references, files })));
    await call("POST", `${GMAIL}/drafts`, { message: { raw, ...(reply ? { threadId: reply.threadId } : {}) } });
    return { webLink: "https://mail.google.com/mail/u/0/#drafts" };
  },

  "calendar.day": async (a, call) => {
    const days = Math.min(7, Math.max(1, Number(a.days) || 1));
    const start = new Date(); start.setHours(0, 0, 0, 0);
    const end = new Date(start); end.setDate(end.getDate() + days);
    const r = await call("GET", `${CAL}/calendars/primary/events?singleEvents=true&orderBy=startTime&maxResults=30&timeZone=${encodeURIComponent(tz())}&timeMin=${encodeURIComponent(start.toISOString())}&timeMax=${encodeURIComponent(end.toISOString())}`);
    return ((r.items ?? []) as any[]).filter((e) => e.status !== "cancelled").map((e) => ({
      ...eventOut(e), organizer: e.organizer?.displayName ?? e.organizer?.email ?? "",
      attendees: ((e.attendees ?? []) as any[]).slice(0, 12).map((x) => x.displayName ?? x.email),
    }));
  },

  "calendar.view": async (a, call) => {
    const from = isoDate(a.start, "start date"), to = isoDate(a.end ?? a.start, "end date");
    const start = new Date(`${from}T00:00:00`); const end = new Date(`${to}T00:00:00`); end.setDate(end.getDate() + 1);
    const days = (end.getTime() - start.getTime()) / 86_400_000;
    if (days < 1 || days > 31) throw new GraphInputError("Please pick at most 31 days.");
    const r = await call("GET", `${CAL}/calendars/primary/events?singleEvents=true&orderBy=startTime&maxResults=150&timeZone=${encodeURIComponent(tz())}&timeMin=${encodeURIComponent(start.toISOString())}&timeMax=${encodeURIComponent(end.toISOString())}`);
    return {
      timeZone: tz(),
      events: ((r.items ?? []) as any[]).filter((e) => e.status !== "cancelled").map((e) => ({
        ...eventOut(e), allDay: Boolean(e.start?.date), showAs: e.transparency === "transparent" ? "free" : "busy",
        organizer: { name: e.organizer?.displayName ?? e.organizer?.email ?? "", email: e.organizer?.email ?? "" },
        attendees: ((e.attendees ?? []) as any[]).slice(0, 25).map((x) => ({ name: x.displayName ?? x.email, email: x.email, response: x.responseStatus ?? "" })),
      })),
    };
  },

  "meeting.findTimes": async (a, call) => {
    const who = emails(a.attendees); const mins = minutes(a.minutes);
    const from = new Date(); from.setMinutes(from.getMinutes() + 30, 0, 0);
    const until = new Date(from); until.setDate(until.getDate() + 7);
    const fb = await call("POST", `${CAL}/freeBusy`, { timeMin: from.toISOString(), timeMax: until.toISOString(), timeZone: tz(), items: [{ id: "primary" }, ...who.map((id) => ({ id }))] });
    const unseen: string[] = [];
    const busy = Object.entries((fb.calendars ?? {}) as Record<string, { busy?: { start: string; end: string }[]; errors?: unknown[] }>)
      .flatMap(([id, c]) => { if (c.errors?.length) unseen.push(id); return (c.busy ?? []).map((b) => [new Date(b.start).getTime(), new Date(b.end).getTime()] as const); });
    const slots: { start: string; end: string; confidence: number }[] = [];
    const t = new Date(from); t.setMinutes(t.getMinutes() < 30 ? 30 : 60, 0, 0);
    while (t < until && slots.length < 6) {
      const day = t.getDay(), h = t.getHours() + t.getMinutes() / 60, endT = new Date(t.getTime() + mins * 60_000);
      const workday = day >= 1 && day <= 5 && h >= 9 && endT.getHours() + endT.getMinutes() / 60 <= 17 && endT.getDate() === t.getDate();
      if (workday && !busy.some(([s, e]) => s < endT.getTime() && e > t.getTime())) slots.push({ start: toLocal(t), end: toLocal(endT), confidence: unseen.length ? 50 : 100 });
      t.setMinutes(t.getMinutes() + 30);
    }
    return { reason: unseen.length ? `Couldn't see the calendars of ${unseen.join(", ")}; these times are free for everyone else.` : slots.length ? "" : "No shared free time in the next 7 working days.", slots };
  },

  "meeting.create": async (a, call, confirm) => {
    const subject = str(a.subject, "meeting title", 255), start = localDateTime(a.start), mins = minutes(a.minutes);
    const who = emails(a.attendees), agenda = optStr(a.agenda, "agenda"), location = optStr(a.location, "location", 255);
    const meet = a.teams !== false;
    const endD = new Date(start); endD.setMinutes(endD.getMinutes() + mins);
    if (!(await confirm(meet ? "Book this Google Meet meeting?" : "Book this meeting?", `${subject}\n${start.replace("T", " ").slice(0, 16)} for ${mins} minutes${location ? `\nWhere: ${location}` : ""}\nWith: ${who.join(", ") || "just you"}\n\n${who.length ? "Invitations go to everyone listed." : "It goes in your calendar."}`))) return { cancelled: true };
    const e = await call("POST", `${CAL}/calendars/primary/events?sendUpdates=all${meet ? "&conferenceDataVersion=1" : ""}`, {
      summary: subject, description: agenda, ...(location ? { location } : {}),
      start: { dateTime: start, timeZone: tz() }, end: { dateTime: toLocal(endD), timeZone: tz() },
      attendees: who.map((email) => ({ email })),
      ...(meet ? { conferenceData: { createRequest: { requestId: `vera-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, conferenceSolutionKey: { type: "hangoutsMeet" } } } } : {}),
    });
    return { webLink: e.htmlLink, joinUrl: e.hangoutLink ?? null };
  },
  // Her own meeting is cancelled (everyone is told); someone else's is declined (the organizer is told).
  "meeting.cancel": async (a, call, confirm) => {
    const eventId = encodeURIComponent(str(a.eventId, "meeting", 1024));
    const e = await call("GET", `${CAL}/calendars/primary/events/${eventId}`);
    const organizer = Boolean(e.organizer?.self);
    const when = String(e.start?.dateTime ?? e.start?.date ?? "").replace("T", " ").slice(0, 16);
    const others = (e.attendees ?? []).filter((x: { self?: boolean }) => !x.self).length;
    if (!(await confirm(organizer ? "Cancel this meeting?" : "Decline this meeting?",
      `${e.summary ?? "(no title)"}\n${when}\n\n${organizer ? (others ? "Everyone invited gets a cancellation." : "It's removed from your calendar.") : "The organizer is told you won't come."}`))) return { cancelled: true };
    if (organizer) await call("DELETE", `${CAL}/calendars/primary/events/${eventId}?sendUpdates=all`);
    else await call("PATCH", `${CAL}/calendars/primary/events/${eventId}?sendUpdates=all`, {
      attendees: (e.attendees ?? []).map((x: { self?: boolean }) => (x.self ? { ...x, responseStatus: "declined" } : x)),
    });
    return { done: organizer ? "cancelled" : "declined" };
  },
  "meeting.move": async (a, call, confirm) => {
    const eventId = encodeURIComponent(str(a.eventId, "meeting", 1024));
    const start = localDateTime(a.start);
    const e = await call("GET", `${CAL}/calendars/primary/events/${eventId}`);
    if (!e.organizer?.self) throw new GraphInputError("Only the organizer can move this meeting. Ask the organizer, or decline it and suggest another time.");
    const oldStart = new Date(String(e.start?.dateTime)), oldEnd = new Date(String(e.end?.dateTime));
    const mins = a.minutes == null ? Math.max(5, Math.round((oldEnd.getTime() - oldStart.getTime()) / 60_000) || 30) : minutes(a.minutes);
    const endD = new Date(start); endD.setMinutes(endD.getMinutes() + mins);
    const others = (e.attendees ?? []).filter((x: { self?: boolean }) => !x.self).length;
    if (!(await confirm("Move this meeting?", `${e.summary ?? "(no title)"}\nFrom ${String(e.start?.dateTime ?? "").replace("T", " ").slice(0, 16)}\nTo ${start.replace("T", " ").slice(0, 16)} for ${mins} minutes\n\n${others ? "Everyone invited gets the new time." : "It's moved in your calendar."}`))) return { cancelled: true };
    await call("PATCH", `${CAL}/calendars/primary/events/${eventId}?sendUpdates=all`, { start: { dateTime: start, timeZone: tz() }, end: { dateTime: toLocal(endD), timeZone: tz() } });
    return { moved: true };
  },
};

/** Google errors in plain words. */
export function friendlyGoogleError(status: number): string {
  if (status === 401) return "Your Gmail connection has timed out. Reconnect Gmail in Connections, it takes one click.";
  if (status === 403) return "Your Google account doesn't allow this. It may need your Google Workspace admin's approval.";
  if (status === 404) return "That item isn't there any more.";
  if (status === 429) return "Google is busy. Please try again in a minute.";
  return "Google didn't answer. Please try again.";
}

export function makeGoogleCall(token: string): Call {
  return async (method, url, body) => {
    const res = await fetch(url, { method, headers: { Authorization: `Bearer ${token}`, Accept: "application/json", ...(body ? { "Content-Type": "application/json" } : {}) }, body: body ? JSON.stringify(body) : undefined });
    if (!res.ok) throw new GraphInputError(friendlyGoogleError(res.status));
    return res.status === 204 ? {} : res.json();
  };
}
